'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';


const TESTIMONIALS = [
  {
    quote: 'Got 20 new clients in week one. All showed up. Made $400 on first-timers alone.',
    name: 'Marcus J.',
    shop: "Marcus's Fade House, Atlanta",
    initials: 'MJ',
    color: 'hsl(43 72% 58%)',
    metric: '$400',
    metricLabel: 'Week 1 Revenue',
  },
  {
    quote: 'This is cash flow generation. Clients are pre-committed and ready to come in.',
    name: 'Derek W.',
    shop: 'Fresh Cuts Barbershop, Miami',
    initials: 'DW',
    color: 'hsl(25 95% 55%)',
    metric: '20',
    metricLabel: 'Clients / Month',
  },
  {
    quote: 'Stop hoping people show up. Have them pre-committed. The follow-up system is key.',
    name: 'Jamal T.',
    shop: 'The Cut Standard, Chicago',
    initials: 'JT',
    color: 'hsl(199 89% 52%)',
    metric: '94%',
    metricLabel: 'Show Rate',
  },
  {
    quote: 'No more marketing guessing. Pay for results. Get paid on day one.',
    name: 'Andre S.',
    shop: 'Silva Barbershop, Los Angeles',
    initials: 'AS',
    color: 'hsl(160 84% 42%)',
    metric: '$2,700',
    metricLabel: 'Client LTV',
  },
];

const FAQ = [
  {
    q: 'I already use Booksy / Squire / GlossGenius',
    a: 'Perfect. We integrate directly. Leads auto-populate into your existing calendar. No new workflows.',
  },
  {
    q: "What if clients don't come back?",
    a: "That\'s every client. We already made you money before you find out. The structured follow-up system dramatically improves rebooking rates.",
  },
  {
    q: 'Do I control which clients I accept?',
    a: 'Yes. Set your availability, service preferences, and geographic areas. We match clients to your exact criteria.',
  },
  {
    q: "Why isn't every barber doing this?",
    a: "Because most companies sell marketing. We sell outcomes. There's a big difference between \"we'll run your ads\" and \"here are 20 pre-committed clients.\"",
  },
  {
    q: 'What happens after the first 20 clients?',
    a: 'You buy another batch. Most barbers see 60–70% rebooking from the first batch, so your chair fills itself over time.',
  },
];

export default function BarberLandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouse = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouse, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.id) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouse);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <title>The Barber Sphere — 20 Booked Clients, Day One</title>
      <meta name="description" content="Get 20 pre-committed barber clients delivered to your chair. Pay $20, they pay $40, you profit $20 before they leave. No ads, no waiting, no guessing." />
      <meta name="keywords" content="barber clients, barbershop leads, barber marketing, fade haircut, barber growth" />
      <meta property="og:title" content="The Barber Sphere — 20 Booked Clients, Day One" />
      <meta property="og:description" content="Get 20 pre-committed barber clients delivered to your chair. Profit on day one." />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />

      <div
        className="min-h-screen text-white overflow-x-hidden"
        style={{ background: 'hsl(222 20% 5%)', fontFamily: '"DM Sans", system-ui, sans-serif' }}
      >
        {/* ── NAV ── */}
        <nav
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-14 py-4 transition-all duration-500"
          style={{
            background: scrollY > 60 ? 'hsl(222 20% 5% / 0.95)' : 'transparent',
            backdropFilter: scrollY > 60 ? 'blur(24px)' : 'none',
            borderBottom: scrollY > 60 ? '1px solid hsl(43 72% 55% / 0.1)' : 'none',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, hsl(43 72% 55% / 0.2), hsl(43 72% 55% / 0.05))',
                border: '1px solid hsl(43 72% 55% / 0.35)',
              }}
            >
              <span className="font-black text-sm" style={{ color: 'hsl(43 72% 62%)' }}>✦</span>
            </div>
            <span className="font-black text-sm tracking-tight text-white">The Barber Sphere</span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm" style={{ color: 'hsl(220 5% 50%)' }}>
            <a href="#how" className="hover:text-white transition-colors duration-200">How It Works</a>
            <a href="#proof" className="hover:text-white transition-colors duration-200">Results</a>
            <a href="#faq" className="hover:text-white transition-colors duration-200">FAQ</a>
          </div>

          <div className="flex items-center gap-3">
            <Link
              href="/barber-login"
              className="hidden md:block text-sm font-medium px-4 py-2 rounded-xl transition-all duration-200"
              style={{ color: 'hsl(220 5% 55%)', background: 'hsl(220 15% 10% / 0.6)', border: '1px solid hsl(220 15% 16%)' }}
            >
              Log In
            </Link>
            <Link
              href="/barber-signup"
              className="text-sm font-bold px-5 py-2.5 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, hsl(43 72% 58%), hsl(35 85% 50%))',
                color: 'hsl(222 20% 5%)',
                boxShadow: '0 4px 20px hsl(43 72% 55% / 0.35)',
              }}
            >
              Get Started
            </Link>
          </div>
        </nav>

        {/* ── HERO ── */}
        <section ref={heroRef} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-20">
          <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, hsl(222 22% 7%) 0%, hsl(222 18% 4%) 50%, hsl(240 20% 5%) 100%)' }} />
          {mounted && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: `radial-gradient(700px circle at ${mousePos.x}px ${mousePos.y}px, hsl(43 72% 55% / 0.06), transparent 60%)` }}
            />
          )}
          <div
            className="absolute inset-0 opacity-[0.025] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              backgroundSize: '200px 200px',
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
            <span
              className="font-black select-none whitespace-nowrap"
              style={{
                fontSize: 'clamp(80px, 18vw, 280px)',
                color: 'transparent',
                WebkitTextStroke: '1px hsl(43 72% 55% / 0.04)',
                transform: `translateY(${scrollY * 0.2}px)`,
                letterSpacing: '-0.04em',
              }}
            >
              SPHERE
            </span>
          </div>
          <div
            className="absolute top-20 right-16 w-96 h-96 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, hsl(43 72% 55% / 0.12) 0%, transparent 70%)',
              transform: `translateY(${scrollY * 0.15}px)`,
              filter: 'blur(60px)',
            }}
          />
          <div
            className="absolute bottom-24 left-4 w-72 h-72 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, hsl(25 95% 55% / 0.07) 0%, transparent 70%)',
              transform: `translateY(${scrollY * -0.1}px)`,
              filter: 'blur(70px)',
            }}
          />

          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-14 w-full">
            <div className="flex items-center gap-3 mb-8">
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{ background: 'hsl(43 72% 55% / 0.1)', border: '1px solid hsl(43 72% 55% / 0.2)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: 'hsl(43 72% 68%)' }}>
                  Accepting Barbers — Seattle
                </span>
              </div>
            </div>

            <h1
              className="font-black leading-[0.88] tracking-tight mb-8"
              style={{ fontSize: 'clamp(52px, 9.5vw, 128px)', letterSpacing: '-0.035em' }}
            >
              <span className="block text-white">Profitable Clients</span>
              <span
                className="block"
                style={{
                  background: 'linear-gradient(135deg, hsl(43 82% 68%) 0%, hsl(35 90% 55%) 45%, hsl(25 95% 58%) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                on Day One.
              </span>
            </h1>

            <div className="flex flex-col lg:flex-row lg:items-end gap-14">
              <div className="max-w-xl">
                <p className="text-lg leading-relaxed mb-10" style={{ color: 'hsl(220 5% 52%)', lineHeight: '1.7' }}>
                  You pay $20. Give $20 off. They pay $40. You make $20 before they leave the chair.{' '}
                  <span className="font-semibold" style={{ color: 'hsl(220 5% 75%)' }}>No ads. No waiting. No guessing.</span>
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    href="/barber-signup"
                    className="group inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 hover:scale-105 active:scale-95"
                    style={{
                      background: 'linear-gradient(135deg, hsl(43 82% 60%), hsl(35 90% 50%))',
                      color: 'hsl(222 20% 5%)',
                      boxShadow: '0 8px 40px hsl(43 72% 55% / 0.4)',
                    }}
                  >
                    Get Started Today
                    <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                  <a
                    href="#how"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-base transition-all duration-200 hover:scale-105"
                    style={{ border: '1px solid hsl(220 15% 16%)', color: 'hsl(220 5% 60%)', background: 'hsl(220 15% 8% / 0.6)' }}
                  >
                    See How It Works
                  </a>
                </div>

                <div className="flex items-center gap-4 mt-8">
                  <div className="flex -space-x-2.5">
                    {['MJ', 'DW', 'JT', 'AS'].map((initials, i) => (
                      <div
                        key={initials}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black border-2"
                        style={{
                          background: `hsl(${43 + i * 18} 72% ${42 + i * 4}%)`,
                          borderColor: 'hsl(222 20% 5%)',
                          color: 'hsl(222 20% 5%)',
                          zIndex: 4 - i,
                        }}
                      >
                        {initials}
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">
                      <span style={{ color: 'hsl(43 72% 62%)' }}>20+ barbers</span> already enrolled
                    </div>
                    <div className="text-xs" style={{ color: 'hsl(220 5% 38%)' }}>Avg. 20 new clients/month</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:ml-auto lg:w-64">
                {[
                  { value: '$20', label: 'Your Cost', sub: 'per client', accent: 'hsl(220 5% 55%)', border: 'hsl(220 15% 16%)', glow: false },
                  { value: '$40', label: 'They Pay', sub: 'day one', accent: 'hsl(43 82% 62%)', border: 'hsl(43 72% 55% / 0.35)', glow: true },
                  { value: '$20', label: 'Your Profit', sub: 'before they leave', accent: 'hsl(160 84% 45%)', border: 'hsl(160 84% 42% / 0.3)', glow: false },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl px-5 py-4 flex items-center gap-4 relative overflow-hidden"
                    style={{
                      background: stat.glow ? 'hsl(222 20% 8%)' : 'hsl(222 20% 7%)',
                      border: `1px solid ${stat.border}`,
                      boxShadow: stat.glow ? '0 0 40px hsl(43 72% 55% / 0.1)' : 'none',
                    }}
                  >
                    {stat.glow && (
                      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, hsl(43 72% 58%), transparent)' }} />
                    )}
                    <p className="text-3xl font-black font-mono" style={{ color: stat.accent }}>{stat.value}</p>
                    <div>
                      <p className="text-sm font-bold text-white">{stat.label}</p>
                      <p className="text-xs" style={{ color: 'hsl(220 5% 40%)' }}>{stat.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none" style={{ background: 'linear-gradient(to top, hsl(222 20% 5%), transparent)' }} />
        </section>

        {/* ── MARQUEE ── */}
        <div className="relative overflow-hidden py-4 border-y" style={{ background: 'hsl(43 72% 55% / 0.04)', borderColor: 'hsl(43 72% 55% / 0.1)' }}>
          <div style={{ animation: 'marquee 24s linear infinite', display: 'flex', gap: '4rem', whiteSpace: 'nowrap' }}>
            {[
              'Pre-Committed Clients', 'Day One Profit', 'Calendar Integration', 'Zero No-Shows', 'Booksy Compatible',
              'Squire Compatible', 'GlossGenius Compatible', 'In-Person Sourcing', '$20 Per Client',
              'Pre-Committed Clients', 'Day One Profit', 'Calendar Integration', 'Zero No-Shows', 'Booksy Compatible',
              'Squire Compatible', 'GlossGenius Compatible', 'In-Person Sourcing', '$20 Per Client',
            ].map((item, i) => (
              <span key={i} className="text-xs font-bold tracking-[0.2em] uppercase flex items-center gap-4" style={{ color: 'hsl(43 72% 58% / 0.55)' }}>
                {item}
                <span style={{ color: 'hsl(43 72% 55% / 0.25)' }}>✦</span>
              </span>
            ))}
          </div>
          <style>{`@keyframes marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
        </div>

        {/* ── BENTO STATS ── */}
        <section className="py-24 px-6 md:px-14 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {[
              { val: '94%', label: 'Show Rate', sub: 'vs 60% industry avg', color: 'hsl(160 84% 45%)', span: '' },
              { val: '$2,700', label: 'Client LTV', sub: 'avg lifetime value', color: 'hsl(43 82% 62%)', span: '' },
              { val: '20+', label: 'Barbers Active', sub: 'in Seattle alone', color: 'hsl(199 89% 52%)', span: '' },
              { val: 'Day 1', label: 'First Profit', sub: 'guaranteed', color: 'hsl(25 95% 58%)', span: '' },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-3xl p-6 relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
                style={{ background: 'hsl(222 20% 7%)', border: '1px solid hsl(220 15% 12%)' }}
              >
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${s.color}60, transparent)` }} />
                <p className="text-4xl font-black font-mono mb-1" style={{ color: s.color }}>{s.val}</p>
                <p className="text-sm font-bold text-white">{s.label}</p>
                <p className="text-xs mt-0.5" style={{ color: 'hsl(220 5% 40%)' }}>{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Wide bento card */}
          <div
            className="rounded-3xl p-8 md:p-12 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, hsl(222 22% 8%), hsl(222 18% 6%))', border: '1px solid hsl(43 72% 55% / 0.15)' }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, hsl(43 72% 58% / 0.6), transparent)' }} />
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(43 72% 55% / 0.08) 0%, transparent 70%)', filter: 'blur(40px)' }} />
            <div className="flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: 'hsl(43 72% 58%)' }}>The Math Is Simple</p>
                <h3 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ letterSpacing: '-0.02em' }}>
                  20 clients × $20 profit = <span style={{ color: 'hsl(43 82% 62%)' }}>$400 guaranteed</span>
                </h3>
                <p style={{ color: 'hsl(220 5% 50%)' }}>Before rebooking. Before referrals. Before your regular clients even know you got new ones.</p>
              </div>
              <Link
                href="/barber-signup"
                className="shrink-0 inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, hsl(43 82% 60%), hsl(35 90% 50%))',
                  color: 'hsl(222 20% 5%)',
                  boxShadow: '0 8px 40px hsl(43 72% 55% / 0.35)',
                }}
              >
                Claim Your Batch
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section id="how" className="relative py-32 overflow-hidden" style={{ background: 'hsl(222 20% 4%)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 80% at 80% 50%, hsl(43 72% 55% / 0.04), transparent)' }} />
          <div className="max-w-7xl mx-auto px-6 md:px-14 relative z-10">
            <div className="mb-16">
              <p className="text-xs font-bold tracking-[0.25em] uppercase mb-4" style={{ color: 'hsl(43 72% 58%)' }}>The Process</p>
              <h2 className="font-black leading-[0.95] tracking-tight" style={{ fontSize: 'clamp(32px, 4.5vw, 60px)', letterSpacing: '-0.03em' }}>
                How it actually works
              </h2>
            </div>

            {/* Asymmetric process layout */}
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5 rounded-3xl p-8 relative overflow-hidden" style={{ background: 'hsl(222 20% 8%)', border: '1px solid hsl(43 72% 55% / 0.15)' }}>
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, hsl(43 72% 58% / 0.5), transparent)' }} />
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ background: 'hsl(43 72% 55% / 0.12)', border: '1px solid hsl(43 72% 55% / 0.2)' }}>
                  <span className="text-xl" style={{ color: 'hsl(43 72% 62%)' }}>✦</span>
                </div>
                <h3 className="text-2xl font-black text-white mb-3">We source the client</h3>
                <p style={{ color: 'hsl(220 5% 50%)', lineHeight: '1.7' }}>Our reps find real people in your city who need a barber. They offer a $20 discount voucher. Client commits on the spot — no online ads, no cold DMs.</p>
                <div className="mt-6 pt-6 border-t" style={{ borderColor: 'hsl(220 15% 12%)' }}>
                  <p className="text-xs font-bold tracking-widest uppercase" style={{ color: 'hsl(43 72% 55%)' }}>In-person sourcing only</p>
                </div>
              </div>

              <div className="lg:col-span-7 grid grid-rows-2 gap-6">
                <div className="rounded-3xl p-7 relative overflow-hidden" style={{ background: 'hsl(222 20% 7%)', border: '1px solid hsl(220 15% 12%)' }}>
                  <div className="flex items-start gap-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'hsl(199 89% 52% / 0.12)', border: '1px solid hsl(199 89% 52% / 0.2)' }}>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: 'hsl(199 89% 52%)' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-white mb-2">Client drops into your calendar</h3>
                      <p className="text-sm" style={{ color: 'hsl(220 5% 50%)', lineHeight: '1.6' }}>Works with Booksy, Squire, GlossGenius, or any calendar. You see the name, time, and service. No extra apps.</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl p-7 relative overflow-hidden" style={{ background: 'hsl(222 20% 7%)', border: '1px solid hsl(220 15% 12%)' }}>
                  <div className="flex items-start gap-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'hsl(160 84% 42% / 0.12)', border: '1px solid hsl(160 84% 42% / 0.2)' }}>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: 'hsl(160 84% 45%)' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-white mb-2">Client pays $40. You keep $20 profit.</h3>
                      <p className="text-sm" style={{ color: 'hsl(220 5% 50%)', lineHeight: '1.6' }}>They used their $20 voucher. You charged $40 for a $60 service. You already made money before they walk out.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section id="proof" className="relative py-32 overflow-hidden" style={{ background: 'hsl(222 20% 5%)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 50%, hsl(43 72% 55% / 0.04), transparent)' }} />
          <div className="max-w-7xl mx-auto px-6 md:px-14 relative z-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
              <div>
                <p className="text-xs font-bold tracking-[0.25em] uppercase mb-4" style={{ color: 'hsl(43 72% 58%)' }}>Barbers are making this work</p>
                <h2 className="font-black leading-[0.95] tracking-tight" style={{ fontSize: 'clamp(32px, 4.5vw, 60px)', letterSpacing: '-0.03em' }}>
                  Real barbers.<br />
                  <span style={{ color: 'hsl(43 72% 60%)' }}>Real money on day one.</span>
                </h2>
              </div>
              <div className="flex gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className="rounded-full transition-all duration-300"
                    style={{ width: i === activeTestimonial ? '28px' : '8px', height: '8px', background: i === activeTestimonial ? 'hsl(43 72% 58%)' : 'hsl(220 15% 18%)' }}
                  />
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-4">
              <div
                className="lg:col-span-7 rounded-3xl p-8 relative overflow-hidden"
                style={{
                  background: 'hsl(222 20% 8%)',
                  border: `1px solid ${TESTIMONIALS[activeTestimonial].color}35`,
                  boxShadow: `0 20px 80px ${TESTIMONIALS[activeTestimonial].color}12`,
                  transition: 'border-color 0.5s, box-shadow 0.5s',
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${TESTIMONIALS[activeTestimonial].color}, transparent)` }} />
                <div className="flex gap-0.5 mb-6">
                  {[1, 2, 3, 4, 5].map((s) => <span key={s} className="text-base" style={{ color: 'hsl(43 72% 58%)' }}>★</span>)}
                </div>
                <p className="text-xl md:text-2xl font-semibold leading-relaxed mb-8" style={{ color: 'hsl(220 5% 80%)', lineHeight: '1.5' }}>
                  "{TESTIMONIALS[activeTestimonial].quote}"
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center text-sm font-black"
                      style={{ background: `${TESTIMONIALS[activeTestimonial].color}18`, color: TESTIMONIALS[activeTestimonial].color, border: `1px solid ${TESTIMONIALS[activeTestimonial].color}28` }}
                    >
                      {TESTIMONIALS[activeTestimonial].initials}
                    </div>
                    <div>
                      <p className="font-bold text-white">{TESTIMONIALS[activeTestimonial].name}</p>
                      <p className="text-sm" style={{ color: 'hsl(220 5% 45%)' }}>{TESTIMONIALS[activeTestimonial].shop}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-black font-mono" style={{ color: TESTIMONIALS[activeTestimonial].color }}>{TESTIMONIALS[activeTestimonial].metric}</p>
                    <p className="text-xs" style={{ color: 'hsl(220 5% 40%)' }}>{TESTIMONIALS[activeTestimonial].metricLabel}</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-4">
                {TESTIMONIALS.filter((_, i) => i !== activeTestimonial).slice(0, 2).map((t) => (
                  <div
                    key={t.name}
                    className="rounded-3xl p-6 relative overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
                    style={{ background: 'hsl(222 20% 7%)', border: '1px solid hsl(220 15% 12%)' }}
                    onClick={() => setActiveTestimonial(TESTIMONIALS.indexOf(t))}
                  >
                    <div className="flex gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map((s) => <span key={s} className="text-xs" style={{ color: 'hsl(43 72% 58%)' }}>★</span>)}
                    </div>
                    <p className="text-sm leading-relaxed mb-4" style={{ color: 'hsl(220 5% 60%)' }}>"{t.quote}"</p>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center text-[10px] font-black" style={{ background: `${t.color}15`, color: t.color, border: `1px solid ${t.color}22` }}>
                        {t.initials}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">{t.name}</p>
                        <p className="text-xs" style={{ color: 'hsl(220 5% 40%)' }}>{t.shop}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq" className="relative py-32 overflow-hidden" style={{ background: 'hsl(222 20% 5%)' }}>
          <div className="max-w-3xl mx-auto px-6 md:px-14 relative z-10">
            <div className="text-center mb-16">
              <p className="text-xs font-bold tracking-[0.25em] uppercase mb-4" style={{ color: 'hsl(43 72% 58%)' }}>Common Questions</p>
              <h2 className="font-black leading-[0.95] tracking-tight" style={{ fontSize: 'clamp(32px, 4.5vw, 56px)', letterSpacing: '-0.03em' }}>
                Real answers.
              </h2>
            </div>

            <div className="space-y-3">
              {FAQ.map((item, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden transition-all duration-300"
                  style={{
                    background: openFaq === i ? 'hsl(222 20% 8%)' : 'hsl(222 20% 7%)',
                    border: openFaq === i ? '1px solid hsl(43 72% 55% / 0.2)' : '1px solid hsl(220 15% 12%)',
                  }}
                >
                  <button
                    className="w-full flex items-center justify-between px-6 py-5 text-left"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-white pr-4">{item.q}</span>
                    <span
                      className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300"
                      style={{
                        background: openFaq === i ? 'hsl(43 72% 55% / 0.15)' : 'hsl(220 15% 14%)',
                        color: openFaq === i ? 'hsl(43 72% 62%)' : 'hsl(220 5% 45%)',
                        transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)',
                      }}
                    >
                      +
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p style={{ color: 'hsl(220 5% 55%)', lineHeight: '1.7' }}>{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="relative py-32 overflow-hidden" style={{ background: 'hsl(222 20% 4%)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 50%, hsl(43 72% 55% / 0.07), transparent)' }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 40% 40% at 50% 50%, hsl(43 72% 55% / 0.04), transparent)' }} />
          <div className="max-w-4xl mx-auto px-6 md:px-14 text-center relative z-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8"
              style={{ background: 'hsl(43 72% 55% / 0.1)', border: '1px solid hsl(43 72% 55% / 0.2)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: 'hsl(43 72% 68%)' }}>Limited spots — Seattle</span>
            </div>
            <h2
              className="font-black leading-[0.9] tracking-tight mb-6"
              style={{ fontSize: 'clamp(40px, 7vw, 96px)', letterSpacing: '-0.035em' }}
            >
              <span className="text-white">Your chair.</span>
              <br />
              <span style={{ background: 'linear-gradient(135deg, hsl(43 82% 68%), hsl(35 90% 55%), hsl(25 95% 58%))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Full. Now.
              </span>
            </h2>
            <p className="text-lg mb-10 max-w-xl mx-auto" style={{ color: 'hsl(220 5% 50%)', lineHeight: '1.7' }}>
              Join barbers across Seattle who stopped waiting for clients and started getting paid on day one.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/barber-signup"
                className="group inline-flex items-center justify-center gap-3 px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 hover:scale-105 active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, hsl(43 82% 60%), hsl(35 90% 50%))',
                  color: 'hsl(222 20% 5%)',
                  boxShadow: '0 12px 50px hsl(43 72% 55% / 0.45)',
                }}
              >
                Get Started Today
                <svg className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link
                href="/barber-login"
                className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-2xl font-semibold text-lg transition-all duration-200 hover:scale-105"
                style={{ border: '1px solid hsl(220 15% 16%)', color: 'hsl(220 5% 60%)', background: 'hsl(220 15% 8% / 0.6)' }}
              >
                Log In
              </Link>
            </div>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="py-12 px-6 md:px-14" style={{ background: 'hsl(222 20% 3%)', borderTop: '1px solid hsl(220 15% 9%)' }}>
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'hsl(43 72% 55% / 0.12)', border: '1px solid hsl(43 72% 55% / 0.22)' }}
              >
                <span className="text-xs font-black" style={{ color: 'hsl(43 72% 62%)' }}>✦</span>
              </div>
              <span className="font-black text-sm text-white">The Barber Sphere</span>
            </div>
            <div className="flex items-center gap-6 text-sm" style={{ color: 'hsl(220 5% 32%)' }}>
              <Link href="/barber-login" className="hover:text-white transition-colors">Barber Login</Link>
              <Link href="/barber-signup" className="hover:text-white transition-colors">Sign Up</Link>
              <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            </div>
            <p className="text-xs" style={{ color: 'hsl(220 5% 30%)' }}>© 2026 The Barber Sphere. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
